import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { DropDown, SimpleCross } from "../../assets/icons";
import ButtonLoader from "../../atoms/buttonLoader";
import { AllocateProject, getProjectsData } from "../../services/service";
import { useFormik } from "formik";
import * as Yup from "yup";
import SuccessSideBar from "./SuccessSideBar";

const AddProjectSideBar = ({ visible, onClose, workerId, fetchWorker, workerData }) => {
  const { Option } = Select;
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isSuccessbarVisible, setSuccessbarVisible] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);

  const handleGetProjects = async () => {
    setProjectsLoading(true);
    try {
      const response = await getProjectsData("", 1, 100, "");
      if (response?.success) {
        setProjects(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching workers data:", error);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    handleGetProjects();
  }, [workerId]);

  // Filter out already assigned projects
  useEffect(() => {
    if (projects && workerData) {
      const allocatedProjectIds = workerData?.detail?.AllocatedProjects?.map(project => project.id) || [];
      const filteredProjects = projects.filter(project => !allocatedProjectIds.includes(project.id));
      setAvailableProjects(filteredProjects);
    } else {
      setAvailableProjects(projects);
    }
  }, [projects, workerData]);

  const formik = useFormik({
    initialValues: {
      project: "",
    },
    validationSchema: Yup.object({
      project: Yup.string().required("Project is required"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setFieldTouched }) => {
      setFieldTouched("project", true);
      if (!values.project) {
        return;
      }

      setIsLoading(true);
      const data = {
        user_id: workerId,
        project_id: values.project,
      };
      try {
        const response = await AllocateProject(data);
        if (response?.success) {
          setSuccessbarVisible(true);
          fetchWorker(true);
          // Close the drawer after showing success sidebar for a few seconds
          setTimeout(() => {
            onClose();
          }, 3000); // 3 second delay to show the success sidebar
        } else {
          console.error("Error in API response:", response?.message);
        }

      } catch (error) {
        console.error("Error assigning worker:", error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Reset form when modal is closed
  useEffect(() => {
    if (!visible) {
      formik.resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30"></div>
      <div className="absolute top-0 right-0 w-[400px] h-full bg-white shadow-lg" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center p-4">
        <h2 className="text-[20px] font-semibold text-[#1E3A8A]">
          Assign Project
        </h2>
        <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>
          {SimpleCross}
        </button>
      </div>
      <div className="p-4 -mt-5">
        <form onSubmit={formik.handleSubmit}>
          <Select
            placeholder="Select Project"
            className={`w-full h-[52px] rounded-[4px] border 
      border-[#E9E9E9]
   focus:outline-none mb-4`}
            allowClear={false}
            suffixIcon={DropDown}
            value={formik.values.project || undefined}
            onChange={(value) => formik.setFieldValue("project", value)}
            loading={projectsLoading}
          >
            {availableProjects?.length > 0 ? (
              availableProjects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.title}
                </Option>
              ))
            ) : (
              <Option disabled value="no-projects">
                No available projects to assign
              </Option>
            )}
          </Select>

          {formik.errors.project && formik.touched.project && (
            <div className="text-red-500 text-sm mb-4">
              {formik.errors.project}
            </div>
          )}
          <div className="flex gap-4">
            <button
              type="submit"
              className="bg-[#F97316] hover:bg-orange-600 text-white py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={isLoading || availableProjects?.length === 0}
            >
              {isLoading ? <ButtonLoader /> : "Assign Project"}
            </button>
          </div>
        </form>
      </div>
      <SuccessSideBar
        visible={isSuccessbarVisible}
        onClose={() => setSuccessbarVisible(false)}
        closeCreate={onClose}
        project={true}
      />
      </div>
    </div>
  );
};

export default AddProjectSideBar;
